import {
  BackNForthSectionProps,
  CarouselItems,
  ComparisonCardItemType,
  ComparisonCardSectionType,
  ComponentImage,
  FeatureGridItem,
  QuoteCtaLinkType,
  QuoteType,
} from '@lucid/components-react';
import {
  Drupal_FieldParagraphFeatureGridSectionFieldFeatureGridItems,
  Drupal_ParagraphCarouselItem,
  Drupal_ParagraphComparisonCard,
  Drupal_ParagraphFeatureGridItem,
  Drupal_ParagraphNewBackNForthSection,
  Drupal_ParagraphNewBackNForthSections,
  Drupal_ParagraphQuote,
  Drupal_ParagraphSeoImage,
  Drupal_TaxonomyTermAnimations,
} from 'generated/drupal-graphql';

export const mapFeatureGridItems = (
  item: Drupal_FieldParagraphFeatureGridSectionFieldFeatureGridItems,
): FeatureGridItem => {
  const entity = item?.entity as Drupal_ParagraphFeatureGridItem;
  const image = entity.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
  return {
    description: entity.fieldSectionDescription?.processed,
    id: entity.id,
    title: entity.fieldTitle,
    image: image && {
      imageAlt: image?.fieldAltText || '',
      imageSrc: image?.fieldImageLink,
    },
    cta: entity.fieldCta && {
      linkText: entity.fieldCta?.title,
      linkUrl: entity.fieldCta?.url?.path,
    },
  } as FeatureGridItem;
};

export const mapBackNForthSections = (backNForthData: Drupal_ParagraphNewBackNForthSections) =>
  backNForthData.fieldBackNForthSections?.reduce<BackNForthSectionProps[]>((previous, current) => {
    const currentSection = current.entity as Drupal_ParagraphNewBackNForthSection;
    const currentImage = currentSection.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        cta: {
          linkUrl: currentSection.fieldCtaLink?.url?.path as string,
          linkText: currentSection.fieldCtaLink?.title as string,
        },
        description: {
          processed: currentSection.fieldSectionDescription?.processed as string,
        },
        image: {
          imageAlt: (currentImage.fieldAltText as string) || '',
          imageSrc: currentImage.fieldImageLink as string,
        },
        title: currentSection.fieldTitle as string,
        video: currentSection.fieldVideoLink as string,
      },
    ];
  }, []) || [];

export const mapQuoteCarouselData = (quoteCarouselItems: Drupal_ParagraphQuote[]) =>
  quoteCarouselItems?.reduce<QuoteType[]>((previous, current) => {
    const quoteCta = current.fieldQuoteCtaLink?.entity as QuoteCtaLinkType;
    return [
      ...previous,
      {
        bottomAttribution: current.fieldAttribution,
        quote: current.fieldQuote,
        cta: quoteCta,
      },
    ];
  }, []) || [];

export const mapComparisonCards = (comparisonCards: Drupal_ParagraphComparisonCard[]) =>
  comparisonCards?.map(card => {
    const comparisonCardTitle = card?.fieldComparisonCardTitle;
    const comparisonSections = card?.fieldComparisonSections?.map(
      section => section.entity,
    ) as ComparisonCardSectionType[];
    const comparisonImage = card?.fieldImage?.entity as Drupal_ParagraphSeoImage;
    const comparisonImageNew = {
      imageAlt: comparisonImage?.fieldAltText,
      imageSrc: comparisonImage?.fieldImageLink,
    } as ComponentImage;
    const comparisonCta = card?.fieldCta;

    return {
      fieldComparisonCardTitle: comparisonCardTitle,
      fieldComparisonSections: comparisonSections,
      fieldCta: comparisonCta,
      fieldImage: comparisonImageNew,
    };
  }) as ComparisonCardItemType[];

export const mapCarouselItems = (carouselItems: Drupal_ParagraphCarouselItem[]) =>
  carouselItems.reduce<CarouselItems[]>((previous, current) => {
    const item = current as Drupal_ParagraphCarouselItem;
    const animationData = item.fieldAnimation?.entity as Drupal_TaxonomyTermAnimations;
    const itemImage = item.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        animation: {
          data: animationData?.fieldAnimationData,
        },
        heading: item.fieldHeading!,
        subheading: item.fieldSubtitle!,
        image: {
          imageAlt: itemImage.fieldAltText! || '',
          imageSrc: itemImage.fieldImageLink!,
        },
        footerText: item.fieldCarouselFooterText!,
      },
    ];
  }, []);
